/**
 * Electronics Toolkit – hub page for open-source electronics calculators.
 * Full SEO: meta, canonical, Open Graph, Twitter Card, JSON-LD (WebApplication + BreadcrumbList).
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../../components/SEO';
import {
  OhmsLaw,
  LedResistor,
  ResistorColorCode,
  VoltageDivider,
  ParallelResistance,
  SeriesResistance,
  UnitConverter,
  RcTimeConstant,
} from '../../../components/openSource/tools/electronics';

const ts = () => new Date().toISOString();

const SITE_URL = 'https://rivieratech.solutions';
const TOOLKIT_PATH = '/open-source/tools/electronics';

// Id, translation key, component
const CALC_LIST = [
  { id: 'ohms-law', key: 'ohmsLaw', Component: OhmsLaw },
  { id: 'led-resistor', key: 'ledResistor', Component: LedResistor },
  { id: 'resistor-color-code', key: 'resistorColorCode', Component: ResistorColorCode },
  { id: 'voltage-divider', key: 'voltageDivider', Component: VoltageDivider },
  { id: 'parallel-resistance', key: 'parallelResistance', Component: ParallelResistance },
  { id: 'series-resistance', key: 'seriesResistance', Component: SeriesResistance },
  { id: 'unit-converter', key: 'unitConverter', Component: UnitConverter },
  { id: 'rc-time-constant', key: 'rcTimeConstant', Component: RcTimeConstant },
];

/** JSON-LD WebApplication + BreadcrumbList for Google rich results and discovery */
const getToolkitSchema = () => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebApplication',
      name: 'Electronics Toolkit – Free Online Calculators',
      description: 'Free online electronics calculators: Ohm\'s law, LED resistor, resistor color code, voltage divider, parallel and series resistance, unit converter, RC time constant. Use in browser, no sign-up.',
      url: `${SITE_URL}${TOOLKIT_PATH}`,
      applicationCategory: 'UtilitiesApplication',
      operatingSystem: 'Any',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
      featureList: CALC_LIST.map((c) => c.key).join(', '),
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Open Source', item: `${SITE_URL}/open-source` },
        { '@type': 'ListItem', position: 3, name: 'Tools', item: `${SITE_URL}/open-source` },
        { '@type': 'ListItem', position: 4, name: 'Electronics Toolkit', item: `${SITE_URL}${TOOLKIT_PATH}` },
      ],
    },
  ],
});

const ElectronicsToolkit = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isStandalone = location.pathname === '/open-source/tools/electronics';

  const [activeId, setActiveId] = useState(() => {
    const hash = typeof window !== 'undefined' ? window.location.hash.slice(1) : '';
    const found = CALC_LIST.some((c) => c.id === hash);
    return found ? hash : CALC_LIST[0].id;
  });

  useEffect(() => {
    const hash = (location.hash || '').slice(1);
    if (hash && CALC_LIST.some((c) => c.id === hash) && hash !== activeId) {
      console.log(`[${ts()}] [ElectronicsToolkit] sync from hash=${hash}`);
      setActiveId(hash);
    }
  }, [location.hash, activeId]);

  const activeCalc = useMemo(() => CALC_LIST.find((c) => c.id === activeId) || CALC_LIST[0], [activeId]);

  console.log(`[${ts()}] [ElectronicsToolkit] render, activeId=${activeId}`);

  const handleSelect = (id) => {
    console.log(`[${ts()}] [ElectronicsToolkit] select calculator id=${id}`);
    setActiveId(id);
    if (typeof window !== 'undefined') window.location.hash = id;
  };

  // Standalone: light, professional layout. Embedded: keep compact.
  const isLight = isStandalone;

  return (
    <div className={isLight ? 'rounded-xl bg-slate-100 p-4 sm:p-6 border border-gray-800' : ''}>
      {/* When standalone this page is wrapped by ToolLayout, which supplies the
          breadcrumb and Prev/Next; its container already pads the top, so no
          pt-24 here. The rounded panel keeps the light calculator visually
          contained inside the dark frame. */}
      <div className={`max-w-5xl mx-auto ${isStandalone ? 'pb-8 px-1 sm:px-4' : ''}`}>
        {isStandalone && (
          <SEO
            title="Free Electronics Calculator – Ohm's Law, LED Resistor, Voltage Divider & More"
            description="Free online electronics calculators: Ohm's law, LED resistor, resistor color code, voltage divider, parallel/series resistance, unit converter, RC time constant. No sign-up. Use suffixes like 1uF, 10kΩ, 20mA."
            keywords="electronics calculator, Ohm's law calculator, LED resistor calculator, resistor color code, voltage divider calculator, parallel resistance, series resistance, RC time constant, unit converter electronics, free online calculator"
            url={TOOLKIT_PATH}
            type="website"
            schema={getToolkitSchema()}
          />
        )}

        <div className="mb-6">
          {isStandalone ? (
            <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-2">
              {t('openSource.tools.electronics.title')}
            </h1>
          ) : (
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">
              {t('openSource.tools.electronics.title')}
            </h2>
          )}
          {isStandalone && (
            <p className="text-slate-600 text-xs mb-2 inline-block px-2.5 py-1 rounded-md bg-slate-200/80 border border-slate-300">
              {t('openSource.tools.electronics.valueProp')}
            </p>
          )}
          <p className="text-slate-600 text-sm mb-2">
            {t('openSource.tools.electronics.subtitle')}
          </p>
          {isStandalone && (
            <p className="text-slate-500 text-sm max-w-2xl">
              {t('openSource.tools.electronics.seoIntro')}
            </p>
          )}
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          <aside className="md:w-56 flex-shrink-0" aria-label={t('openSource.tools.electronics.calcsNavLabel')}>
            <nav className={`sticky top-24 space-y-0.5 rounded-xl p-1 ${isLight ? 'bg-white border border-slate-200 shadow-sm' : ''}`} aria-label={t('openSource.tools.electronics.calcsNavLabel')}>
              {CALC_LIST.map((calc) => (
                <button
                  key={calc.id}
                  type="button"
                  onClick={() => handleSelect(calc.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    activeId === calc.id
                      ? isLight
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-gray-800 text-silver-light border border-silver-medium'
                      : isLight
                        ? 'text-slate-700 hover:bg-slate-100'
                        : 'text-silver-medium hover:text-silver-light hover:bg-gray-900'
                  }`}
                >
                  {t(`openSource.tools.electronics.calcs.${calc.key}`)}
                </button>
              ))}
            </nav>
          </aside>

          <main className="flex-1 min-w-0">
            <div className={`rounded-xl border p-6 ${isLight ? 'bg-white border-slate-200 shadow-md' : 'border-gray-800 bg-off-black'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isLight ? 'text-slate-900' : 'text-silver-light'}`}>
                {t(`openSource.tools.electronics.calcs.${activeCalc.key}`)}
              </h3>
              <activeCalc.Component />
            </div>
            {/* Cross-tool links are now the Prev/Next bar in ToolLayout. */}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ElectronicsToolkit;
